import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const GoogleIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const StarIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 20 20" fill="#FBBC05">
    <path d="M10 1l2.39 4.84L17.5 6.8l-3.75 3.66.89 5.17L10 13.14l-4.64 2.5.89-5.17L2.5 6.8l5.11-.96L10 1z"/>
  </svg>
);

const GoogleReviewBubble = ({ googleReviewUrl, accentColor, shopSlug }) => {
  const storageKey = `review_dismissed_${shopSlug || 'default'}`;
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === 'true'; } catch { return false; }
  });

  useEffect(() => {
    if (!googleReviewUrl || dismissed) return;

    // Show banner once after 3 seconds, auto-hide after 8 seconds
    const showTimer = setTimeout(() => setShowBanner(true), 3000);
    const hideTimer = setTimeout(() => setShowBanner(false), 11000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [googleReviewUrl, dismissed]);

  if (!googleReviewUrl) return null;

  const handleBubbleClick = () => {
    if (showBanner) {
      window.open(googleReviewUrl, '_blank');
    } else {
      setShowBanner(true);
      setTimeout(() => {
        if (!dismissed) setShowBanner(false);
      }, 8000);
    }
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setShowBanner(false);
    setDismissed(true);
    try { localStorage.setItem(storageKey, 'true'); } catch {}
  };

  const handleReviewClick = () => {
    window.open(googleReviewUrl, '_blank');
  };

  return (
    <div className="fixed z-50" style={{ bottom: '90px', right: '16px' }} data-testid="google-review-bubble">
      {/* Banner card */}
      <div
        className="absolute right-0 mb-3 transition-all duration-500 ease-out"
        style={{
          bottom: '60px',
          opacity: showBanner ? 1 : 0,
          transform: showBanner ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.95)',
          pointerEvents: showBanner ? 'auto' : 'none',
        }}
        data-testid="google-review-banner"
      >
        <div
          className="rounded-2xl shadow-2xl overflow-hidden"
          style={{
            width: '260px',
            background: 'white',
            border: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors z-10"
            data-testid="google-review-dismiss"
          >
            <X className="w-3 h-3 text-gray-500" />
          </button>

          {/* Header */}
          <div className="px-4 pt-4 pb-2">
            <p className="text-gray-900 font-bold text-base">Votre avis compte !</p>
            <div className="flex gap-0.5 mt-1">
              {[...Array(5)].map((_, i) => <StarIcon key={i} />)}
            </div>
          </div>

          {/* Google review link */}
          <div className="px-4 pb-4">
            <button
              onClick={handleReviewClick}
              className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors"
              data-testid="google-review-link"
            >
              <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center shrink-0 shadow-sm">
                <GoogleIcon />
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-900 text-sm">Google</p>
                <p className="text-gray-500 text-xs">Laisser un avis</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Floating bubble */}
      <button
        onClick={handleBubbleClick}
        className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95"
        style={{
          background: 'linear-gradient(135deg, #4285F4, #34A853)',
          boxShadow: '0 4px 15px rgba(66, 133, 244, 0.4)',
        }}
        data-testid="google-review-fab"
      >
        <GoogleIcon />
      </button>
    </div>
  );
};

export default GoogleReviewBubble;
